const escapeRegex = /[&'"<>]/g
const emailRegex =
  /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i // eslint-disable-line no-useless-escape
import { transform, isEqual, isObject } from 'lodash-es'

const escapeMap: Record<string, string> = {
  '&': '&amp;',
  '"': '&quot;',
  "'": '&#39;',
  '<': '&lt;',
  '>': '&gt;',
}

const friendlyHours = {
  1: '1 hour',
  24: '24 hours',
  48: '48 hours',
  168: '1 week',
  336: '2 weeks',
}

function escapeMapFn(m: string): string {
  return escapeMap[m]
}

function escape(val: string): string {
  if (typeof val !== 'string') return ''
  return val.replace(escapeRegex, escapeMapFn)
}

function linebreaks(input: string): string {
  if (!input) return ''
  return input.replace(/\n/g, '<br>')
}

function escapelinebreaks(val: string): string {
  return linebreaks(escape(val))
}

function friendlyFileSize(bytes: number): string {
  const unit = 1024
  if (bytes < unit) {
    return (bytes % 1 === 0 ? bytes : bytes.toFixed(2)) + ' B'
  }
  const exp = Math.log(bytes) / Math.log(unit)
  const pre = ' ' + 'KMGTPE'[exp - 1] + 'B'
  const n = bytes / Math.pow(unit, exp)
  return (n % 1 === 0 ? n : n.toFixed(2)) + pre
}

function safeText(userInput: string): string {
  return escapelinebreaks(userInput)
}

function isValidEmail(email: string): boolean {
  return emailRegex.test(String(email).toLowerCase())
}

/**
 * Deep diff between two objects, using lodash
 * @param  {Object} item    Object compared
 * @param  {Object} other   Object to compare with
 * @return {Object}        Return a new object representing the diff
 */
function objDifference(
  item: Record<string, unknown>,
  other: Record<string, unknown>
): object {
  return transform(
    item,
    (result: Record<string, unknown>, value: unknown, key: string) => {
      if (!isEqual(value, other[key])) {
        result[key] =
          isObject(value) && isObject(other[key])
            ? objDifference(
                value as Record<string, unknown>,
                other[key] as Record<string, unknown>
              )
            : value
      }
    }
  )
}

function getQuestionnaireNumbering(currentNo: string, increment = 1): string {
  const splits = currentNo.split('.')
  // new number by increasing last number by increment
  return splits
    .map((x, i) => (i == splits.length - 1 ? +x + increment : x))
    .join('.')
}
/*
Transform a word to title case
*/
function toTitleCase(word: string): string {
  if (!word) return ''
  const trimmed = word.toLowerCase().trim()
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
}

/*
Transform snake case to individual words, title cased
*/
function snakeCaseToTitle(input: string): string {
  return input
    .split('_')
    .map((word) => toTitleCase(word))
    .join(' ')
}

/**
 * Return a readable text color (black or white) for a given hex background.
 * Ensures adequate contrast using WCAG relative luminance heuristic.
 */
export function needsWhiteText(hex: string): boolean {
  const cleaned = hex.replace('#', '')
  // Only accept 6-digit hex; fallback to black text
  if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return false
  const r = parseInt(cleaned.slice(0, 2), 16) / 255
  const g = parseInt(cleaned.slice(2, 4), 16) / 255
  const b = parseInt(cleaned.slice(4, 6), 16) / 255

  const srgbToLin = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  const R = srgbToLin(r)
  const G = srgbToLin(g)
  const B = srgbToLin(b)
  const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B

  const contrastWithBlack = (luminance + 0.05) / 0.05
  const contrastWithWhite = 1.05 / (luminance + 0.05)

  const blackOK = contrastWithBlack >= 4.5
  const whiteOK = contrastWithWhite >= 4.5
  if (blackOK && !whiteOK) return false
  if (whiteOK && !blackOK) return true
  return contrastWithBlack <= contrastWithWhite
}

export {
  friendlyFileSize,
  safeText,
  isValidEmail,
  friendlyHours,
  emailRegex,
  objDifference,
  getQuestionnaireNumbering,
  snakeCaseToTitle,
}
